import { takeLatest, takeEvery, take, select, put } from 'redux-saga/effects'
import { burst } from 'common/utilities/saga-watchers'
import {
  USER_SUCCESS,
  USER_FAILURE,
  SNOWPLOW_TRACK_PAGE_VIEW,
  SNOWPLOW_SET_GLOBAL_CONTEXTS,
  SNOWPLOW_TRACK_CONTENT_OPEN,
  SNOWPLOW_TRACK_IMPRESSION,
  VARIANTS_SAVE,
  SESS_GUID_RECEIVED,
  SESS_GUID_HYDRATE
} from 'actions'
import {
  createUserEntity,
  apiUserEntity,
  createContentEntity,
  createUiEntity,
  createFeatureFlagEntity,
  UI_COMPONENT_CARD
} from 'common/snowplow/entities'
import {
  createImpressionEvent,
  createContentOpenEvent,
  createVariantEnrollEvent
} from 'common/snowplow/events'
import {
  snowplowTrackPageView,
  sendCustomSnowplowEvent,
  setSnowplowGlobalContexts
} from 'common/api/snowplow-analytics'

const initialState = {
  globalContexts: [],
  hashedSessGuid: undefined,
  hashedUserId: undefined
}

/** ACTIONS
 --------------------------------------------------------------- */
export const sessGuidHydrate = (sess_guid) => ({
  type: SESS_GUID_HYDRATE,
  sess_guid
})

export const trackPageView = () => ({
  type: SNOWPLOW_TRACK_PAGE_VIEW
})

export const trackContentOpen = (destination, trigger, position, item) => {
  return {
    type: SNOWPLOW_TRACK_CONTENT_OPEN,
    destination,
    trigger,
    position,
    item
  }
}
export const trackImpression = (component, requirement, position, item) => {
  return {
    type: SNOWPLOW_TRACK_IMPRESSION,
    component,
    requirement,
    position,
    item
  }
}

/** REDUCERS
 --------------------------------------------------------------- */
export const snowplowReducers = (state = initialState, action) => {
  switch (action.type) {
    case USER_SUCCESS: {
      const { user } = action

      return { ...state, hashedUserId: user?.user_id }
    }

    case USER_FAILURE: {
      return {
        ...state,
        hashedUserId: null
      }
    }

    case SESS_GUID_HYDRATE:
    case SESS_GUID_RECEIVED: {
      const { sess_guid } = action

      return { ...state, hashedSessGuid: sess_guid }
    }

    case SNOWPLOW_SET_GLOBAL_CONTEXTS: {
      const { globalContexts } = action

      return { ...state, globalContexts }
    }

    default:
      return state
  }
}

/** SAGAS :: WATCHERS
 --------------------------------------------------------------- */

export const snowplowSagas = [
  takeLatest(SNOWPLOW_TRACK_PAGE_VIEW, firePageView),
  // we do not want batches of set global contexts to each be processed
  burst(100, SNOWPLOW_SET_GLOBAL_CONTEXTS, setGlobalContexts),
  takeEvery(SNOWPLOW_TRACK_CONTENT_OPEN, fireContentOpen),
  takeEvery(SNOWPLOW_TRACK_IMPRESSION, fireImpression),
  takeLatest(VARIANTS_SAVE, fireVariantEnroll)
]

/** SAGA :: RESPONDERS
 --------------------------------------------------------------- */
function* firePageView() {
  yield waitForGlobalContextsDependencies()

  yield snowplowTrackPageView()
}

function* fireVariantEnroll({ variants }) {
  yield waitForGlobalContextsDependencies()

  for (let flag in variants) {
    const variantEnrollEvent = createVariantEnrollEvent()
    const featureFlagEntity = createFeatureFlagEntity(flag, variants[flag])

    yield sendCustomSnowplowEvent(variantEnrollEvent, [featureFlagEntity])
  }
}

function* fireContentOpen({ destination, trigger, position, item }) {
  yield waitForGlobalContextsDependencies()

  const contentOpenEvent = createContentOpenEvent(destination, trigger)

  const contentEntity = createContentEntity(item?.save_url, item?.resolved_id)

  const uiEntity = createUiEntity({
    type: UI_COMPONENT_CARD,
    hierarchy: 0,
    identifier: 'web-discover-card',
    index: position
  })

  const snowplowEntities = [contentEntity, uiEntity]

  sendCustomSnowplowEvent(contentOpenEvent, snowplowEntities)
}

function* fireImpression({ component, requirement, position, item }) {
  yield waitForGlobalContextsDependencies()

  const impressionEvent = createImpressionEvent(component, requirement)

  const contentEntity = createContentEntity(item?.save_url, item?.resolved_id)

  const uiEntity = createUiEntity({
    type: UI_COMPONENT_CARD,
    hierarchy: 0,
    identifier: 'web-discover-card',
    index: position
  })

  const snowplowEntities = [contentEntity, uiEntity]

  sendCustomSnowplowEvent(impressionEvent, snowplowEntities)
}

function* setGlobalContexts() {
  const globalContexts = yield select((state) => state.snowplow.globalContexts)

  yield setSnowplowGlobalContexts(globalContexts)
}

function* waitForGlobalContextsDependencies() {
  let state = yield select()

  // don't set global contexts if the ones we have are already complete
  // assumes we only set global contexts once
  if (state.snowplow.globalContexts.length) return

  let userId = state.snowplow.hashedUserId
  let sessGuid = state.snowplow.hashedSessGuid

  // userId === null is valid state and signifies USER_FAILURE has returned
  // and we can pass this loop
  while (userId === undefined || !sessGuid) {
    // watch for these events
    yield take([
      SESS_GUID_HYDRATE,
      SESS_GUID_RECEIVED,
      USER_SUCCESS,
      USER_FAILURE
    ])

    const state = yield select()

    userId = state.snowplow.hashedUserId
    sessGuid = state.snowplow.hashedSessGuid
  }

  // add User entity to Snowplow global context
  const userEntity = createUserEntity(userId, sessGuid)

  // set Snowplow global contexts
  yield put({
    type: SNOWPLOW_SET_GLOBAL_CONTEXTS,
    globalContexts: [userEntity, apiUserEntity]
  })
}
